<script async setup lang="ts">
import { mapAccoItem } from '@/composables/filterpage.helpers'
import { CULTURE, uspsOrShortDescription } from '@/composables/helpers'
import type { IAccoItem } from '@/composables/interfaces/apiInterfaces'
import t from '@/i18n/translations'
import { computed } from 'vue'
import AccoItemEnquiryButton from './AccoItemEnquiryButton.vue'
import AccoItemImages from './AccoItemImages.vue'
import AccoItemPrice from './AccoItemPrice.vue'
import AccoItemRating from './AccoItemRating.vue'
import AccoItemRibbon from './AccoItemTopHotel.vue'

const props = defineProps<{
  item: IAccoItem
}>()

const accoItem = computed(() => mapAccoItem(props.item))
const accoUrl = accoItem.value.accoUrl
</script>

<template>
  <div class="relative min-h-12 w-full rounded border bg-white">
    <div class="flex min-h-64">
      <div class="min-w-1/3 z-20 w-1/3">
        <div class="relative h-full overflow-hidden">
          <AccoItemImages :images="accoItem.images"></AccoItemImages>
        </div>
      </div>
      <div class="flex w-full justify-between gap-2 p-4">
        <div class="left-side flex w-3/5 flex-col gap-1">
          <AccoItemRating :is-superior="accoItem.isSuperior" :rating="accoItem.rating" :symbol-class="accoItem.symbolClass" />
          <a
            class="after:absolute after:inset-0 after:z-10"
            target="_blank"
            :href="accoUrl"
            :key="accoItem.companyId"
            @click="$window.siteEvent?.('company-micropage', 'filter-page', item.gaqLabel)"
          >
            <div class="text-xl font-bold leading-5 decoration-primary decoration-2 underline-offset-2 hover:underline">{{ accoItem.companyName }}</div>
          </a>
          <div class="mb-3">
            <div class="text-sm font-normal">{{ accoItem.location }}</div>
          </div>
          <div v-if="uspsOrShortDescription(item) === true">
            <p class="short-desc relative m-0 line-clamp-4 overflow-hidden text-[80%]">
              {{ accoItem.shortDescription }}
            </p>
          </div>
          <div v-else>
            <ul class="bidi-isolate mb-4 me-0 ms-0 list-disc ps-5 text-[80%]">
              <li v-for="usp in accoItem.usps">{{ usp }}</li>
            </ul>
          </div>
        </div>
        <div class="right-side flex w-2/5 flex-col items-end justify-between text-right">
          <div class="flex flex-col leading-5" v-if="accoItem.assessmentRating != '0'">
            <div class="text-sm font-bold">
              {{ accoItem.assessmentRating }}
              {{ accoItem.assessmentNote }}
            </div>
            <div class="text-[13px] font-normal">
              {{ accoItem.ratings }} {{ t(`${CULTURE}.acco.ratings[${accoItem.ratings > 1 ? 'plural' : 'singular'}]`) }}
            </div>
          </div>
          <div class="z-20 flex flex-col justify-end gap-2 md:mt-auto">
            <AccoItemPrice :min-price="accoItem.minPrice" />
            <AccoItemEnquiryButton :cid="item.companyId" @track="$window.siteEvent?.('button', 'enquiry-direct', item.gaqLabel)" />
          </div>
        </div>
      </div>
    </div>
    <AccoItemRibbon :is-tophotel="accoItem.isTophotel" />
  </div>
</template>
